import { useEffect, useState } from 'react';
import {
  FormProvider,
  SubmitHandler,
  useFieldArray,
  useForm,
} from 'react-hook-form';
import { Flex, Spacer } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { CountryCode } from '@shinetools/geo-library';
import { z } from 'zod';

import asBentoPage from 'common/bento/hoc/asBentoPage';
import Accordion from 'components/_core/Accordion';
import Heading from 'components/_core/Heading';
import OBNavigation from 'features/Onboarding/components/OBNavigation/OBNavigation';
import OBStickyButton from 'features/Onboarding/components/OBStickyButton/OBStickyButton';

import zodLocales from '../../../../../../common/zod/locales';
import { FRANCE_COUNTRY_CODE } from '../../../../../Bento/libs/constants';
import AddTaxResidencyButton from './components/AddTaxResidency';
import TaxResidencyForm from './components/TaxResidencyForm';
import locales from './locales';

interface TaxResidencyProps {
  isSaving: boolean;
  onSubmit: SubmitHandler<TaxResidencySchema[]>;
}

const TAX_IDENTIFICATION_NUMBER_REGEX = /^[\d\sA-Za-z]+$/;

const foreignTaxResidencySchema = z.object({
  country: z.string().length(2),
  fiscalId: z
    .string()
    .trim()
    .min(1, zodLocales.errors.required)
    .regex(
      TAX_IDENTIFICATION_NUMBER_REGEX,
      locales.taxIdentificationNumberInvalidFormat,
    ),
});
const frenchTaxResidencySchema = z.object({
  country: z.literal(FRANCE_COUNTRY_CODE),
  fiscalId: z.string().length(0),
});

export type TaxResidencySchema = z.infer<
  typeof foreignTaxResidencySchema | typeof frenchTaxResidencySchema
>;

const taxResidenciesSchema = z.object({
  taxResidencies: z.array(
    z.union([foreignTaxResidencySchema, frenchTaxResidencySchema]),
  ),
});
export type TaxResidenciesSchema = z.infer<typeof taxResidenciesSchema>;

/**
 * A form to retrieve the user's tax residencies in two steps.
 * First the user selects their country of tax residence, we call onSubmit a first time to check the country's eligibility.
 * Then we ask for their tax identification number, and call onSubmit a final time to save it.
 * If the country was not eligible, the machine would've handled the navigation to a blocker screen at this point.
 */
const TaxResidencies = asBentoPage<TaxResidencyProps>(
  ({ isSaving, Layout, onPrev, onSubmit }) => {
    const [indexLoading, setIndexLoading] = useState(-1);
    const form = useForm<TaxResidenciesSchema>({
      defaultValues: {
        taxResidencies: [
          {
            country: FRANCE_COUNTRY_CODE,
            fiscalId: '',
          },
        ],
      },
      mode: 'onChange',
      resolver: zodResolver(taxResidenciesSchema),
    });
    const {
      control,
      formState: { isValid },
      getValues,
    } = form;
    const { append } = useFieldArray({
      control,
      name: 'taxResidencies',
    });

    const addTaxResidency = () => {
      append({ country: '', fiscalId: '' });
    };

    useEffect(() => {
      if (!isSaving && Number(indexLoading) >= 0) {
        setIndexLoading(-1);
      }
    }, [isSaving, indexLoading]);

    const onSubmitCountry = (index: number) => {
      setIndexLoading(index);
      onSubmit(getValues().taxResidencies);
    };

    const getExcludedCountries = (
      taxResidencies: TaxResidencySchema[],
      index: number,
    ): CountryCode[] => {
      return taxResidencies.reduce((acc: CountryCode[], taxResidency, i) => {
        if (i !== index) {
          acc.push(taxResidency.country as CountryCode);
        }
        return acc;
      }, []);
    };

    return (
      <FormProvider {...form}>
        <Layout
          data-testid="tax-residencies"
          nav={<OBNavigation onPrev={onPrev} />}
        >
          <Flex direction="column">
            <Heading marginBottom="space-32" size="lg">
              {locales.title}
            </Heading>

            {getValues().taxResidencies.map((taxResidency, index) => (
              <TaxResidencyForm
                excludedCountries={getExcludedCountries(
                  getValues().taxResidencies,
                  index,
                )}
                index={index}
                isLoading={isSaving && indexLoading === index}
                key={index}
                submitCountry={onSubmitCountry}
              />
            ))}

            {getValues().taxResidencies.length === 1 ? (
              <AddTaxResidencyButton
                marginBottom="space-32"
                marginTop="space-24"
                onClick={addTaxResidency}
              />
            ) : null}

            <Spacer />

            <Accordion>
              <Accordion.Item title={locales.helpTitle}>
                <Accordion.Text>{locales.helpDescription}</Accordion.Text>
              </Accordion.Item>
            </Accordion>

            <Spacer />

            <OBStickyButton
              data-testid="next"
              isDisabled={!isValid}
              isLoading={isSaving}
              onClick={() => onSubmit(getValues().taxResidencies)}
              type="submit"
            >
              {locales.nextButtonLabel}
            </OBStickyButton>
          </Flex>
        </Layout>
      </FormProvider>
    );
  },
);

export default TaxResidencies;
